import type React from "react";

import type { AutomatedSurveyQueueQuestionDetailsListDto } from "@/api/types";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { Badge } from "@/design-system/Badge";
import { Inline } from "@/design-system/Inline";
import { useTranslation } from "@/translations";

interface SurveyQuestionGroupAnswerCardHeaderProps {
  order: number;
  questionTitle: string;
  category: AutomatedSurveyQueueQuestionDetailsListDto["questionGroupType"];
}

export function SurveyQuestionGroupAnswerCardHeader({
  order,
  questionTitle,
  category,
}: SurveyQuestionGroupAnswerCardHeaderProps): React.ReactNode {
  const { t } = useTranslation();
  let categoryLabel = t("page.automated-surveys.types.other");
  switch (category) {
    case "building":
      categoryLabel = t("page.automated-surveys.question-type.building");
      break;
    case "community":
      categoryLabel = t("page.automated-surveys.question-type.community");
      break;
    case "service":
      categoryLabel = t("page.automated-surveys.question-type.service");
      break;
    case "feelAtHome":
      categoryLabel = t("page.automated-surveys.question-type.feeling-at-home");
  }

  return (
    <header className="flex items-start justify-between gap-space-md">
      <h3 className="min-w-0 flex-1 text-heading-3">
        {order}. {questionTitle}
      </h3>
      {category && (
        <span className="shrink-0">
          <Badge variant="info">{categoryLabel}</Badge>
        </span>
      )}
    </header>
  );
}

interface SurveyQuestionGroupAnswerCardRatingProps {
  rating: number | undefined;
}

export function SurveyQuestionGroupAnswerCardRating({
  rating,
}: SurveyQuestionGroupAnswerCardRatingProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <section className="flex w-full flex-col items-center justify-center gap-space-sm">
      <Inline gap="sm" align="center" wrap={false}>
        <Star01Icon size={48} tone="rating" />
        <h1 className="text-heading-1">
          {rating ? rating.toFixed(1) : t("component.surveys-question-group-answer.not-available")}
        </h1>
      </Inline>
      <span className="text-center text-body">{t("component.surveys-question-group-answer.label.avg-rating")}</span>
    </section>
  );
}
